import { z } from "zod";
import { PullRequestStatus } from "./api.ts";
import { GitWorkspaceState, GitPreparation } from "./git-workspace.ts";

export const PrDraft = z.object({
  revision: z.number().int().nonnegative(), title: z.string(), body: z.string(),
  baseBranch: z.string(), isDraft: z.boolean(), commitMessage: z.string(),
  preparationId: z.string().nullable(), sourceHeadSha: z.string().nullable(),
  generated: z.boolean(), updatedAt: z.number(),
});
export type PrDraft = z.infer<typeof PrDraft>;
export const SavePrDraftRequest = z.object({
  expectedRevision: z.number().int().nonnegative(), title: z.string().max(200),
  body: z.string().max(8_000), baseBranch: z.string().min(1).max(250), isDraft: z.boolean(),
  commitMessage: z.string().max(500), preparationId: z.string().nullable(),
  sourceHeadSha: z.string().nullable(), generated: z.boolean(),
});
export type SavePrDraftRequest = z.infer<typeof SavePrDraftRequest>;
export const PrCheck = z.object({
  id: z.string(), name: z.string(), status: z.string(), conclusion: z.string().nullable(),
  url: z.string().nullable(), headSha: z.string(), startedAt: z.string().nullable(),
  completedAt: z.string().nullable(), required: z.boolean().nullable(),
});
export const PrReviewThread = z.object({
  id: z.string(), path: z.string(), line: z.number().nullable(), body: z.string(),
  author: z.string(), url: z.string(), resolved: z.boolean(),
});
export const PrGitHub = z.object({
  status: PullRequestStatus, baseBranch: z.string(), headBranch: z.string(),
  checks: z.array(PrCheck), historicalChecks: z.array(PrCheck), checksComplete: z.boolean(),
  reviewDecision: z.string().nullable(), reviewsAvailable: z.boolean(),
  threads: z.array(PrReviewThread),
  deployments: z.array(z.object({ name: z.string(), state: z.string(), url: z.string().nullable() })),
  metadataRevision: z.string(),
});
export type PrGitHub = z.infer<typeof PrGitHub>;
export const PrAction = z.enum(["commit", "commit-push", "push", "create", "sync-base", "sync-upstream", "continue-sync", "abort-sync"]);
export type PrAction = z.infer<typeof PrAction>;
export const PrOperation = z.object({
  id: z.string(), action: PrAction, phase: z.string(),
  preparationId: z.string().nullable(), draftRevision: z.number().int().nonnegative(),
  status: z.enum(["running", "completed", "failed", "conflicts"]),
  error: z.string().nullable(), sha: z.string().nullable(), pushed: z.boolean(), updatedAt: z.number(),
});
export type PrOperation = z.infer<typeof PrOperation>;
export const RunPrActionRequest = z.object({
  operationId: z.string().min(8).max(64), action: PrAction,
  preparationId: z.string().nullable(), draftRevision: z.number().int().nonnegative(),
});
export type RunPrActionRequest = z.infer<typeof RunPrActionRequest>;
export const PreparePrRequest = z.object({
  baseBranch: z.string().min(1).max(250), paths: z.array(z.string().min(1)).optional(),
  model: z.string().min(1).max(200).optional(), inferCommitConvention: z.boolean().optional(),
  generate: z.boolean(), refresh: z.boolean().optional(), providerId: z.enum(["claude", "codex"]).optional(), templatePath: z.string().optional(),
});
export type PreparePrRequest = z.infer<typeof PreparePrRequest>;
export const UpdatePrMetadataRequest = z.object({
  operationId: z.string().min(8).max(64), draftRevision: z.number().int().positive(),
  expectedMetadataRevision: z.string().min(1),
});
export type UpdatePrMetadataRequest = z.infer<typeof UpdatePrMetadataRequest>;

export const PrWorkspace = z.object({
  draft: PrDraft.nullable(), preparation: GitPreparation.nullable(), github: PrGitHub.nullable(), git: GitWorkspaceState.nullable(),
  operation: PrOperation.nullable(), gitError: z.string().nullable(), githubError: z.string().nullable(),
  repoAuth: z.enum(["none", "github-app"]), canWrite: z.boolean(), agentRunning: z.boolean(),
  mergeMethod: z.enum(["merge", "squash", "rebase"]),
});
export type PrWorkspace = z.infer<typeof PrWorkspace>;
export const PreparePrResponse = z.object({
  preparation: GitPreparation, title: z.string(), body: z.string(), commitMessage: z.string(),
  model: z.string().optional(), generationError: z.string().optional(),
  generated: z.boolean(), templates: z.array(z.object({ path: z.string(), content: z.string() })), providerId: z.string(),
});
export type PreparePrResponse = z.infer<typeof PreparePrResponse>;
